import axios from 'axios'

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL || 'https://sshagentservice.onrender.com',
  timeout: 30000,
})

const getAuthToken = () => {
  return localStorage.getItem('authToken')
}

// Add request interceptor
api.interceptors.request.use(
  config => {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Add response interceptor for better error handling
api.interceptors.response.use(
  response => {
    return response
  },
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default api

/**
 * Get list of users for chat logs
 * Search and list users with optional date filter for recent chat activity
 * Returns users with role badges (A for Agent, C for Customer/Buyer)
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search term for filtering users by name or email
 * @param {string} params.role - Filter by role (A for Agent, C for Customer)
 * @param {number} params.page - Page number for pagination (default: 1)
 * @param {number} params.limit - Number of items per page (default: 10)
 * @param {string} params.dateFilter - Filter by recent chat activity (today, week, month)
 * @returns {Promise} API response with user list and pagination
 * Response format:
 * {
 *   "success": true,
 *   "data": {
 *     "users": [
 *       {
 *         "_id": "507f1f77bcf86cd799439011",
 *         "full_name": "John Doe",
 *         "email": "john@example.com",
 *         "role": "A",
 *         "lastChatDate": "2025-11-12T10:30:00Z"
 *       }
 *     ],
 *     "pagination": {
 *       "total": 50,
 *       "page": 1,
 *       "limit": 10,
 *       "pages": 5
 *     }
 *   }
 * }
 */
export const getChatLogUsers = params => {
  return api.get('/v1/admin/chat-logs/users', {
    params,
  })
}

/**
 * Load chat conversation between selected users
 * Loads chat messages between 2+ selected users (agents/buyers)
 * Requires at least 2 user IDs. Filters by date if provided. Includes GDPR notice.
 * @param {Object} data - Request body
 * @param {Array<string>} data.userIds - Array of user IDs (minimum 2 required)
 * @param {string} data.dateFilter - Optional date filter (today, week, month, all)
 * @returns {Promise} API response with chat messages
 * Request format:
 * {
 *   "userIds": ["507f1f77bcf86cd799439011", "507f191e810c19729de860ea"],
 *   "dateFilter": "week"
 * }
 * Response format:
 * {
 *   "success": true,
 *   "data": {
 *     "conversation": [
 *       {
 *         "senderName": "John Doe",
 *         "senderRole": "A",
 *         "senderId": "507f1f77bcf86cd799439011",
 *         "message": "Hello",
 *         "timestamp": "2025-11-12T10:30:00Z",
 *         "read": true
 *       }
 *     ],
 *     "totalMessages": 8,
 *     "gdprNotice": "This conversation data is accessed for support and compliance purposes."
 *   }
 * }
 */
export const loadChatConversation = data => {
  return api.post('/v1/admin/chat-logs/load', data, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

/**
 * Export chat logs to various formats
 * Exports conversation as CSV or JSON. Requires at least 2 user IDs (comma-separated in query).
 * @param {Object} params - Query parameters
 * @param {string} params.userIds - Comma-separated user IDs (minimum 2 required)
 * @param {string} params.format - Export format (csv, json)
 * @param {string} params.dateFilter - Optional date filter (today, week, month, all)
 * @returns {Promise} API response with export file (blob for csv) or JSON data
 * Response format (when format=json):
 * {
 *   "success": true,
 *   "data": {
 *     "conversation": [
 *       {
 *         "senderName": "John Doe",
 *         "senderRole": "A",
 *         "message": "Hello",
 *         "timestamp": "2025-11-12T10:30:00Z",
 *         "read": true
 *       }
 *     ],
 *     "totalMessages": 8,
 *     "gdprNotice": "This conversation data is accessed for support and compliance purposes."
 *   },
 *   "message": "Chat exported successfully"
 * }
 */
export const exportChatLogs = params => {
  const isBlob = params.format === 'csv'
  return api.get('/v1/admin/chat-logs/export', {
    params,
    responseType: isBlob ? 'blob' : 'json',
  })
}

/**
 * Get GDPR consents for a specific user
 * Updates or manages GDPR consent for a specific user (agent/buyer)
 * Allows admins to view consent for data processing, marketing, etc.
 * @param {string} userId - User ID to get consents for
 * @returns {Promise} API response with GDPR consents
 * Response format:
 * {
 *   "success": true,
 *   "data": {
 *     "consents": {
 *       "dataProcessing": true,
 *       "marketing": false,
 *       "analytics": true
 *     },
 *     "notes": "User requested opt-out from marketing"
 *   }
 * }
 */
export const getGDPRConsents = userId => {
  return api.get(`/v1/admin/gdpr/consents/${userId}`)
}

/**
 * Update GDPR consents for a specific user
 * Updates or manages GDPR consent for a specific user (agent/buyer)
 * Allows admins to toggle consent for data processing, marketing, etc. Logs changes for compliance.
 * @param {string} userId - User ID to update consents for
 * @param {Object} data - Consent data
 * @param {Object} data.consents - Consent settings
 * @param {boolean} data.consents.dataProcessing - Consent for data processing
 * @param {boolean} data.consents.marketing - Consent for marketing communications
 * @param {boolean} data.consents.analytics - Consent for analytics tracking
 * @param {string} data.notes - Optional notes about consent changes
 * @returns {Promise} API response
 */
export const updateGDPRConsents = (userId, data) => {
  return api.put(`/v1/admin/gdpr/consents/${userId}`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

/**
 * Get recent activity logs
 * @param {Object} params - Query parameters
 * @param {string} params.userId - Optional specific user ID
 * @param {string} params.activityType - Filter by activity type
 * @param {number} params.page - Page number for pagination
 * @param {number} params.limit - Number of items per page
 * @param {string} params.startDate - Optional start date filter
 * @param {string} params.endDate - Optional end date filter
 * @returns {Promise} API response with recent activities
 */
export const getRecentActivity = params => {
  return api.get('/v1/admin/recent-activity', {
    params,
  })
}

/**
 * Helper function to download exported file
 * @param {Blob} blob - File blob from export API
 * @param {string} filename - Name for the downloaded file
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
